import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import {
  useModals,
  Modal,
  ModalProps,
  Avatar,
  Button,
  ButtonLink,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  ListItem,
  Skeleton,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import useRoleEditService from "@/pages/settings/roles/edit/role-edit-service.ts";
import { getRoleTitle } from "@/pages/settings/roles/role.types.ts";
import { useEmployees } from "@/queries/employees/use-employees.query.ts";
import { useRoles, useRolesCustomField } from "@/queries/settings/use-roles.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiCustomFieldRolesItem, ApiField } from "@/types/custom-field.types.ts";
import { ApiPermission, PermissionType, RoleType } from "@/types/role.types.ts";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

interface EmployeeCustomFieldRolesModalProps extends ModalProps {
  field: ApiField;
}

export function EmployeeCustomFieldRolesModal({
  field,
  ...props
}: EmployeeCustomFieldRolesModalProps) {
  const { getFieldTypeInformation, getFieldTitle } = useCustomFieldsHook();
  const { canIEdit } = usePermissions();
  const { permissionTypesTexts } = useRoleEditService();
  const { mutateAsync: saveRoles, isPending: saveRolesPending } = useRolesCustomField();
  const { close } = useModals();
  const { t } = useTranslation();
  const { id } = props;
  const canEdit = canIEdit(ApiPermission.settings_roles_permissions);
  const { data: roles, isLoading: isRolesLoading } = useRoles(canEdit);
  const { data: employees, isLoading: isEmployeesLoading } = useEmployees(
    {
      page_number: 1,
      edit_api_permissions: [ApiPermission.settings_roles_permissions],
    },
    !canEdit
  );
  const [rolesSettings, setRolesSettings] = useState<Record<number, PermissionType>>({});
  const [initialRolesSettings, setInitialRolesSettings] = useState<Record<number, PermissionType>>(
    {}
  );
  const permissionTypes: PermissionType[] = [
    PermissionType.none,
    PermissionType.view,
    PermissionType.edit,
  ];

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.customFields.rolesModalViewed, {
      [analyticProperties.id]: field.id,
    });
  });

  const [syncedRoles, setSyncedRoles] = useState<typeof roles>();
  if (roles !== syncedRoles) {
    setSyncedRoles(roles);
    const settings: Record<number, PermissionType> = {};

    roles?.forEach((role) => {
      if (role.type !== RoleType.admin) {
        settings[role.id] = PermissionType.none;
      }
    });

    setRolesSettings(settings);
    setInitialRolesSettings(settings);
  }

  const hasChanges = Object.keys(rolesSettings).some(
    (key) => rolesSettings[Number(key)] !== initialRolesSettings[Number(key)]
  );

  const save = async () => {
    if (!field) return;

    const roles: ApiCustomFieldRolesItem[] = [];

    for (const key in rolesSettings) {
      roles.push({
        role_id: Number(key),
        permission_value: rolesSettings[key],
      });
    }

    await saveRoles({
      custom_field_id: field.id,
      items: roles,
    });

    close(id);
  };

  return (
    <Modal
      {...props}
      withCloser={true}
      layout={"stretch"}
      actions={
        <Stack direction={"vertical"} gap={"smd"}>
          {!canEdit ? (
            <Button
              size={"lg"}
              variant={"primary"}
              onClick={() => {
                close(id);
              }}
            >
              {t("custom_fields:ok_got_it")}
            </Button>
          ) : (
            <>
              <Button
                size={"lg"}
                variant={"primary"}
                onClick={hasChanges ? save : () => close(id)}
                isLoading={saveRolesPending}
              >
                {hasChanges ? t("custom_fields:apply_access") : t("common:skip")}
              </Button>
              <ButtonLink
                variant={"plain"}
                target={"_blank"}
                href={"/settings/roles-and-permissions"}
                rightIcon={<FontAwesomeIcon icon="fa-light fa-arrow-up-right-from-square" />}
                className={"font-semibold"}
              >
                {t("custom_fields:manage_roles")}
              </ButtonLink>
            </>
          )}
        </Stack>
      }
      title={
        canEdit ? t("custom_fields:setup_field_permissions") : t("custom_fields:new_field_created")
      }
    >
      <Stack gap={"md"}>
        <Stack className={"pt-4 text-center"} items={"center"} gap={"sm"}>
          <Avatar size={"xl"} emoji={canEdit ? ":partying_face:" : ":face_with_peeking_eye:"} />
          <Title
            header={
              canEdit ? t("custom_fields:new_field_ready") : t("custom_fields:new_field_is_hidden")
            }
            caption={
              canEdit
                ? t("custom_fields:lets_choose")
                : t("custom_fields:new_field_is_hidden_information")
            }
            size={"lg"}
          />
        </Stack>
        <Stack>
          <ListItem preTitle={t("common:title")} title={getFieldTitle(field)} />
          <ListItem
            preTitle={t("custom_fields:field_type")}
            title={getFieldTypeInformation(field.field_type).title}
          />
          <Title
            className={"text-dark/95"}
            size={"xs"}
            paddingTop
            header={
              canEdit ? t("custom_fields:who_should_see") : t("custom_fields:need_help_question")
            }
            caption={canEdit ? t("custom_fields:set_access") : t("custom_fields:who_can_configure")}
          ></Title>
          {!canEdit && isEmployeesLoading ? (
            <Stack>
              <Stack className={"py-4"}>
                <Skeleton className={"h-8 w-8"} />
              </Stack>
              <Stack className={"py-4"}>
                <Skeleton className={"h-8 w-8"} />
              </Stack>
              <Stack className={"py-4"}>
                <Skeleton className={"h-8 w-8"} />
              </Stack>
            </Stack>
          ) : null}
          {canEdit && isRolesLoading ? (
            <Stack>
              <ListItem titleSlot={<Skeleton className={"h-4 w-32"} />} />
              <ListItem titleSlot={<Skeleton className={"h-4 w-32"} />} />
              <ListItem titleSlot={<Skeleton className={"h-4 w-32"} />} />
              <ListItem titleSlot={<Skeleton className={"h-4 w-32"} />} />
              <ListItem titleSlot={<Skeleton className={"h-4 w-32"} />} />
            </Stack>
          ) : null}
          <Stack>
            {canEdit &&
              roles
                ?.filter((role) => role.type !== RoleType.admin)
                .map((role) => (
                  <ListItem
                    key={`roles-${role.id}`}
                    title={getRoleTitle(role)}
                    valueSlot={
                      <DropdownMenu
                        trigger={
                          <Text
                            className={"underline text-dark/95 cursor-pointer hover:no-underline"}
                            tabIndex={0}
                          >
                            {permissionTypesTexts[rolesSettings[role.id] ?? PermissionType.none]}
                          </Text>
                        }
                      >
                        {permissionTypes.map((permission) => (
                          <DropdownMenuItem
                            key={permission}
                            onClick={() => {
                              const settings = { ...rolesSettings };

                              settings[role.id] = permission;

                              setRolesSettings(settings);
                            }}
                            title={permissionTypesTexts[permission]}
                          />
                        ))}
                      </DropdownMenu>
                    }
                  />
                ))}

            {!canEdit &&
              employees?.items?.map((employee) => (
                <Stack className={"py-4"} key={`employee-${employee.id}`}>
                  <EmployeeListItem employee={employee} />
                </Stack>
              ))}
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
}
